import { ModalSDK } from './modal.types'
import Nav, { NavState } from '../nav/nav'
import { Params } from '../params/params.types'
import Pubsub, { PubsubCallback, PubsubEvent } from '../pubsub/pubsub'
import { ConfigResponse } from '../config/config.types'

export const MODAL_LOAD_EVENT = 'load'
export const MODAL_OPEN_EVENT = 'open'
export const MODAL_HOVER_EVENT = 'hover'
export const MODAL_CLOSE_EVENT = 'close'

interface ModalProps {
  win: Window
  /** When it returns true, close() defers to onCloseRequested instead of closing. */
  shouldInterceptClose?: () => boolean
  /** Invoked instead of closing when shouldInterceptClose() returns true. */
  onCloseRequested?: () => void
}

type BodyStyleSnapshot = {
  position: string
  top: string
  left: string
  right: string
  width: string
  overflow: string
  touchAction: string
  overscrollBehavior: string
} | null

export default class Modal {
  private props: ModalProps
  private nav: Nav
  private pubsub: Pubsub<NavState>
  private _isOpen: boolean
  private config: ConfigResponse
  private destroyed = false
  private lockCount
  private scrollY
  private prev: BodyStyleSnapshot = null

  constructor(props: ModalProps) {
    this.lockCount = 0
    this.scrollY = 0
    this.prev = null

    this.props = props
    this.nav = new Nav({ win: this.props.win })

    this.nav.onPopstate(this._onNavPopstate)

    this.pubsub = new Pubsub()
    this._isOpen = !!this.nav.get().campaign

    this.props.win.addEventListener('keydown', this.__handleKeydown)
    document.addEventListener('keydown', this.__handleKeydown)
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    this.nav.destroy()

    this.props.win.removeEventListener('keydown', this.__handleKeydown)
    document.removeEventListener('keydown', this.__handleKeydown)

    this.pubsub.unsubscribeAll()
    this._isOpen = false
  }

  private _onNavPopstate = (event: PubsubEvent<NavState>) => {
    const { campaign } = event.data

    if (campaign) {
      this._isOpen = true
      this.pubsub.publish({ name: MODAL_OPEN_EVENT, data: this.nav.get() })
    } else {
      this._isOpen = false
      this.pubsub.publish({ name: MODAL_CLOSE_EVENT, data: this.nav.get() })
    }
  }

  public open = (campaign: string, params: Params): void => {
    this.lockBodyScroll()

    if (this._isOpen) {
      return
    }

    this.nav.setClassyParams(campaign, params)
    const state = this.nav.get()
    this.pubsub.publish({
      name: MODAL_OPEN_EVENT,
      data: {
        ...state,
        // URL serialization drops empty values, but Whammy needs this explicit clear.
        params: { ...state.params, ...(params.dedication === '' && { dedication: '' }) },
      },
    })
    this._isOpen = true
  }

  public subscribeToOpenEvent = (callback: PubsubCallback<NavState>): string => {
    return this.pubsub.subscribe(MODAL_OPEN_EVENT, callback)
  }

  public close = (): void => {
    if (this._isOpen && this.props.shouldInterceptClose?.()) {
      this.props.onCloseRequested?.()
      return
    }

    this._close()
  }

  /** Closes without consulting shouldInterceptClose. */
  public forceClose = (): void => {
    this._close()
  }

  private _close = (): void => {
    this.unlockBodyScroll()

    if (!this._isOpen) {
      return
    }

    this.nav.removeClassyParams()
    this.pubsub.publish({ name: MODAL_CLOSE_EVENT, data: this.nav.get() })

    this._isOpen = false
  }

  private lockBodyScroll() {
    this.lockCount += 1
    if (this.lockCount > 1) {
      return
    }

    this.scrollY = window.scrollY || window.pageYOffset || 0

    // Save any existing inline styles so we can restore cleanly
    this.prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      touchAction: document.body.style.touchAction,
      overscrollBehavior: document.body.style.overscrollBehavior,
    }

    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    // Helps on Android + prevents overscroll chaining
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'
    document.body.style.overscrollBehavior = 'none'
  }

  private unlockBodyScroll() {
    if (this.lockCount === 0) {
      return
    }

    this.lockCount -= 1
    if (this.lockCount > 0) {
      return
    }

    if (this.prev) {
      document.body.style.position = this.prev.position
      document.body.style.top = this.prev.top
      document.body.style.left = this.prev.left
      document.body.style.right = this.prev.right
      document.body.style.width = this.prev.width
      document.body.style.overflow = this.prev.overflow
      document.body.style.touchAction = this.prev.touchAction
      document.body.style.overscrollBehavior = this.prev.overscrollBehavior
      this.prev = null
    } else {
      // fallback
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
      document.body.style.overscrollBehavior = ''
    }

    window.scrollTo(0, this.scrollY)
  }

  public subscribeToCloseEvent = (callback: PubsubCallback<NavState>): string => {
    return this.pubsub.subscribe(MODAL_CLOSE_EVENT, callback)
  }

  public hover = (campaign: string, params: Params): void => {
    this.pubsub.publish({ name: MODAL_HOVER_EVENT, data: { campaign, params } })
  }

  public subscribeToHoverEvent = (callback: PubsubCallback<NavState>): string => {
    return this.pubsub.subscribe(MODAL_HOVER_EVENT, callback)
  }

  public unsubscribe = (subscriptionKey: string): void => {
    this.pubsub.unsubscribe(subscriptionKey)
  }

  public onLoad = (callback: PubsubCallback<NavState>): void => {
    callback({ name: MODAL_LOAD_EVENT, data: this.nav.get() })
  }

  public getParams = (): Params => this.nav.get().params

  public getCampaign = (): string | undefined => this.nav.get().campaign

  private __handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.close()
    }
  }

  public setConfig = (config: any) => {
    this.config = config
  }
  public getConfig = (): ConfigResponse => this.config

  public getIsOpen = (): boolean => this._isOpen

  public sdk = (): ModalSDK => ({
    open: this.open,
    close: this.close,
    hover: this.hover,
    isOpen: this.getIsOpen,
    getCampaign: this.getCampaign,
    getParams: this.getParams,
    getConfig: this.getConfig,
    setConfig: this.setConfig,
    subscribeToOpenEvent: this.subscribeToOpenEvent,
    subscribeToCloseEvent: this.subscribeToCloseEvent,
    subscribeToHoverEvent: this.subscribeToHoverEvent,
    unsubscribe: this.unsubscribe,
  })
}
